import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import ReactConfetti from 'react-confetti';

const HomeContainer = styled.div`
  min-height: 80vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: url('/img/imagebackground/compressed/bgimg1.avif');
    background-repeat: repeat;
    background-size: 500px;
    opacity: 0.07;
    z-index: -1;
    pointer-events: none;
  }
`;

const HeroSection = styled.section`
  width: 100%;
  background-image: url('/img/imagebackground/compressed/bgimg1.avif');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  color: var(--light-text);
  padding: 80px 20px;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, 
      rgba(74, 144, 226, 0.7), 
      rgba(93, 173, 226, 0.6), 
      rgba(133, 193, 233, 0.5)
    );
    z-index: 1;
  }
  
  &::after {
    content: "";
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 100%;
    height: 50px;
    background-size: cover;
    z-index: 2;
  }
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  margin-bottom: 20px;
  color: var(--light-text);
  position: relative;
  z-index: 3;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.5rem;
  max-width: 800px;
  margin: 0 auto 40px;
  position: relative;
  z-index: 3;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const FeaturesSection = styled.section`
  padding: 80px 20px;
  background-image: url('/img/imagebackground/compressed/bgimg2.jpg');
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  position: relative;
  width: 100%;
  
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(245 248 250 / 0.11);
    z-index: 0;
  }
`;

const FeaturesTitle = styled.h2`
  text-align: center;
  margin-bottom: 60px;
  font-size: 2.5rem;
  color: var(--primary-color);
  position: relative;
  z-index: 1;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 40px;
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const FeatureCard = styled.div`
  background-color: rgba(255, 255, 255, 0.7);
  border-radius: 10px;
  padding: 30px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  text-align: center;
  position: relative;
  z-index: 1;
  backdrop-filter: blur(5px);
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.15);
  }
`;

const FeatureIcon = styled.div`
  font-size: 2.5rem;
  margin-bottom: 20px;
  color: var(--accent-color);
`;

const FeatureTitle = styled.h3`
  margin-bottom: 15px;
  color: var(--primary-color);
`;

const FeatureDescription = styled.p`
  color: var(--text-color);
  margin-bottom: 20px;
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background-color: var(--accent-color);
  color: var(--light-text);
  padding: 12px 25px;
  border-radius: 5px;
  font-weight: 600;
  transition: background-color 0.3s ease;
  position: relative;
  z-index: 3;
  
  &:hover {
    background-color: #c19b2e;
    color: var(--light-text);
  }
`;

const VideoSection = styled.section`
  width: 100%;
  padding: 60px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: rgba(245, 248, 250, 0.8);
`;

const VideoContainer = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  position: relative;
  aspect-ratio: 16 / 9;
`;

const VideoTitle = styled.h2`
  text-align: center;
  margin-bottom: 30px;
  font-size: 2.2rem;
  color: var(--primary-color);
`;

const Home = () => {
  const [windowDimensions, setWindowDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });
  const [showConfetti, setShowConfetti] = useState(true);
  const iframeRef = useRef(null);

  // Scroll en haut de la page au chargement
  useEffect(() => {
    // Scroll en haut de la page
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setWindowDimensions({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);
    
    // Arrêter les confettis après 8 secondes
    const timer = setTimeout(() => {
      setShowConfetti(false);
    }, 8000);

    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timer);
    };
  }, []);

  return (
    <HomeContainer>
      {showConfetti && (
        <ReactConfetti
          width={windowDimensions.width}
          height={windowDimensions.height}
          recycle={false}
          numberOfPieces={500}
          gravity={0.1}
          colors={['#4A90E2', '#5DADE2', '#85C1E9', '#F2D2BD', '#D4AF37', '#E74C3C', '#2ECC71']}
        />
      )}
      <HeroSection>
        <HeroTitle>Bonne Fête des Pères</HeroTitle>
        <HeroSubtitle>
          Papa, ce site est mon cadeau pour toi, créé avec amour pour célébrer à quel point tu es extraordinaire
        </HeroSubtitle>
        <CTAButton to="/surprise">Découvrir ta surprise</CTAButton>
      </HeroSection>
      
      <FeaturesSection>
        <FeaturesTitle>Explore ton site</FeaturesTitle>
        <FeaturesGrid>
          <FeatureCard>
            <FeatureIcon>📸</FeatureIcon>
            <FeatureTitle>Galerie Photo</FeatureTitle>
            <FeatureDescription>
              Une collection de moments précieux à partager et à chérir.
            </FeatureDescription>
            <CTAButton to="/gallery">Voir les photos</CTAButton>
          </FeatureCard>
          
          <FeatureCard>
            <FeatureIcon>💌</FeatureIcon>
            <FeatureTitle>Compliments</FeatureTitle>
            <FeatureDescription>
              Des compliments touchants pour exprimer notre admiration.
            </FeatureDescription>
            <CTAButton to="/compliments">Voir les compliments</CTAButton>
          </FeatureCard>
          
          <FeatureCard>
            <FeatureIcon>📝</FeatureIcon>
            <FeatureTitle>Poèmes</FeatureTitle>
            <FeatureDescription>
              Des mots touchants pour exprimer notre amour et notre gratitude.
            </FeatureDescription>
            <CTAButton to="/poems">Lire les poèmes</CTAButton>
          </FeatureCard>
          
          <FeatureCard>
            <FeatureIcon>🎁</FeatureIcon>
            <FeatureTitle>Surprise 3D</FeatureTitle>
            <FeatureDescription>
              Une expérience interactive spéciale pour la Fête des Pères.
            </FeatureDescription>
            <CTAButton to="/surprise">Voir la surprise</CTAButton>
          </FeatureCard>
        </FeaturesGrid>
      </FeaturesSection>

      <VideoSection>
        <VideoTitle>Une musique pour toi, Papa</VideoTitle>
        <VideoContainer>
          <iframe 
            ref={iframeRef}
            width="100%" 
            height="100%" 
            src="https://www.youtube-nocookie.com/embed/pAgnJDJN4VA?si=Iwn_6HbrAY8b8amE&start=8"
            title="YouTube video player" 
            frameBorder="0" 
            allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin" 
            allowFullScreen
          ></iframe>
        </VideoContainer>
      </VideoSection>
    </HomeContainer>
  );
};

export default Home;
